import React, { useState, useCallback } from 'react'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "../api/authStore"
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import EditNoteIcon from '@mui/icons-material/EditNote'
import NoteEditor from './NoteEditor'
import MobilePaginationActions from './MobilePaginationActions'

const MobileActiveProxiesTable = ({
  activeProxiesList, 
  selectedRow, 
  onRowClick, 
  onDetailsClick,
  apiClient
}) => {
  const { getAccessTokenSilently } = useAuth()
  //notes
  const [editingNoteFor, setEditingNoteFor] = useState(null)
  const [notes, setNotes] = useState({})

  // Автопродление
  const [autoRenewMap, setAutoRenewMap] = useState({})
  const [autoRenewLoading, setAutoRenewLoading] = useState({})

  const getAutoRenew = useCallback((row) => {
    return autoRenewMap[row?.rentalid] !== undefined
      ? autoRenewMap[row?.rentalid]
      : !!row?.autoRenew
  }, [autoRenewMap])

  const handleToggleAutoRenew = useCallback(async (row) => {
    const id = row?.rentalid
    if (!id || autoRenewLoading[id]) return

    const next = !(autoRenewMap[id] !== undefined ? autoRenewMap[id] : !!row?.autoRenew)
    setAutoRenewLoading(prev => ({ ...prev, [id]: true }))
    try {
      await apiClient.setToken(getAccessTokenSilently()).setAutoRenew(id, next)
      setAutoRenewMap(prev => ({ ...prev, [id]: next }))
    } catch (e) {
      // оставляем прежнее состояние при ошибке
    } finally {
      setAutoRenewLoading(prev => ({ ...prev, [id]: false }))
    }
  }, [autoRenewLoading, autoRenewMap, apiClient, getAccessTokenSilently])
  
  // Состояние для пагинации
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10) // 10 по умолчанию
  
  // Возраст прокси из listedDays (кол-во дней в листинге)
  const calculateProxyAge = useCallback((listedDays) => {
    if (listedDays == null) return 'N/A'

    if (listedDays >= 365) return `${Math.floor(listedDays / 365)}y`
    if (listedDays >= 30) return `${Math.floor(listedDays / 30)}m`
    return `${listedDays}d`
  }, [])
  
  // Сортируем прокси по оставшемуся времени (больше времени сверху) для мобильной версии
  const sortedActiveProxiesList = activeProxiesList.sort((a, b) => {
    // Сортируем по времени истечения в убывающем порядке (больше времени сверху)
    const timeA = a.expiresAt ? new Date(a.expiresAt).getTime() : 0
    const timeB = b.expiresAt ? new Date(b.expiresAt).getTime() : 0
    return timeB - timeA
  })

  // Обработчики для пагинации
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Получаем данные для текущей страницы
  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedActiveProxiesList.slice(startIndex, endIndex)
  }

  const handleEditNote = (agentId) => {
    setEditingNoteFor(agentId)
  }

  const handleSaveNote = (agentId, note) => {
    setNotes(prev => ({ ...prev, [agentId]: note }))
    setEditingNoteFor(null)
  }

  const handleCancelNote = () => {
    setEditingNoteFor(null)
  }
  const formatTimeLeft = (expiresAt) => {
    const diffMs = new Date(expiresAt).getTime() - Date.now()
    const minutes = Math.max(0, Math.floor(diffMs / (60 * 1000) % 60))
    const hours = Math.floor(diffMs / (60 * 60 * 1000))
  
    let result = minutes + "m"
    if (hours > 0) {
      result = hours + "h " + result
    }
  
    if (result === "0m") {
      result = "now"
    }
  
    return result
  }

  return (
    <div style={{ padding: 'var(--spacing-sm)' }}>
      {getCurrentPageData().map((row, index) => (
        <div
          key={row?.rentalid || index}
          onClick={() => onRowClick(row)}
          style={{
            background: 'var(--medium-gray)',
            border: '1px solid var(--light-gray)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-sm)',
            cursor: 'pointer',
            borderLeft: selectedRow?.rentalid === row?.rentalid ? '4px solid var(--primary-orange)' : '4px solid transparent',
            transition: 'var(--transition-normal)'
          }}
        >
          {/* Заголовок карточки */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-lg)',
                fontWeight: '600',
                color: 'var(--text-light)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                {row?.proxyinfo?.ip}
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)'
              }}>
                <ReactCountryFlag 
                  countryCode={row?.proxyinfo?.geo?.cc} 
                  svg 
                  style={{width: '16px', height: '12px'}}
                />
                <span>{row?.proxyinfo?.geo?.cc}</span>
                <span>•</span>
                <span>{row?.proxyinfo?.geo?.city}, {row?.proxyinfo?.geo?.reg}</span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDetailsClick(row, e)
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 152, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent'
              }}
            >
              <MoreHorizIcon className="table-proxies-icon shop-active-info-icon" style={{ fontSize: '24px' }} />
            </button>
          </div>

          {/* Детали прокси */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ISP
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)',
                wordBreak: 'break-word'
              }}>
                {row?.proxyinfo?.geo?.isp}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ZIP Code
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.proxyinfo?.geo?.zip || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Age/Activity
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.proxyinfo?.uptime !== undefined ? (
                  <span className="shop-accent-text">
                    {calculateProxyAge(row?.proxyinfo?.listedDays)}/{Math.round(row?.proxyinfo?.uptime)}%
                  </span>
                ):(
                  <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                )}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Users
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.proxyinfo?.users || 0}
              </div>
            </div>
          </div>

          {/* Время истечения */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-sm)',
            background: 'var(--dark-gray)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--light-gray)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '2px'
              }}>
                Expires in
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                fontWeight: '600',
                color: 'var(--primary-orange)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <AutorenewIcon
                  titleAccess={`AutoRenew: ${getAutoRenew(row) ? 'On' : 'Off'}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleAutoRenew(row)
                  }}
                  style={{
                    fontSize: 18,
                    cursor: autoRenewLoading[row?.rentalid] ? 'wait' : 'pointer',
                    fill: getAutoRenew(row) ? 'url(#goldGradient)' : 'var(--text-muted)',
                    opacity: autoRenewLoading[row?.rentalid] ? 0.5 : 1
                  }}
                />
                <span>{formatTimeLeft(row?.expiresAt)}</span>
              </div>
            </div>
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              textAlign: 'right'
            }}>
              <div>Proxy ID: {row?.rentalid}</div>
            </div>
          </div>

          {/* Заметка */}
          {editingNoteFor === row?.proxyid ? (
            <NoteEditor
              proxy={row}
              apiClient={apiClient}
              currentNote={notes[row?.proxyid] ?? row?.note}
              onSave={(note) => handleSaveNote(row?.proxyid, note)}
              onCancel={handleCancelNote}
            />
          ) : (
            <div style={{
              marginTop: 'var(--spacing-sm)',
              padding: 'var(--spacing-sm)',
              background: 'var(--dark-gray)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--light-gray)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--spacing-xs)'
              }}>
                <span style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  fontWeight: '500'
                }}>
                  📝 Note
                </span>
                <button
                  onClick={() => handleEditNote(row?.proxyid)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-orange)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    padding: '4px 6px',
                    borderRadius: 'var(--radius-sm)',
                    transition: 'var(--transition-normal)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--medium-gray)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  <EditNoteIcon className="table-proxies-icon shop-active-info-icon" style={{ fontSize: 20 }} />
                </button>
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: notes[row?.proxyid] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                fontStyle: notes[row?.proxyid] || row?.note ? 'normal' : 'italic',
                lineHeight: '1.4'
              }}>
                {(notes[row?.proxyid] || row?.note || 'No note added').length > 10 
                    ? (notes[row?.proxyid] || row?.note || 'No note added').substring(0, 10) + '...'
                    : (notes[row?.proxyid] || row?.note || 'No note added')
                }
              </div>
            </div>
          )}
        </div>
      ))}
      
      {/* Пагинация для мобильной версии */}
      {sortedActiveProxiesList.length > rowsPerPage && (
        <div style={{
          marginTop: 'var(--spacing-lg)',
          padding: 'var(--spacing-md)',
          background: 'var(--medium-gray)',
          border: '1px solid var(--light-gray)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-md)',
          alignItems: 'center'
        }}>
          {/* Строка с количеством строк на странице */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--spacing-sm)' 
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Rows per page:
            </span>
            <select 
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-light)',
                padding: '6px 8px',
                fontSize: 'var(--text-sm)',
                minWidth: '60px'
              }}
            >
              {[10, 20, 30, 40, 50].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          {/* Навигация */}
          <MobilePaginationActions 
            page={page}
            rowsPerPage={rowsPerPage}
            count={sortedActiveProxiesList.length}
            onPageChange={handleChangePage}
          />
        </div>
      )}
    </div>
  )
}

export default MobileActiveProxiesTable
